import { useMutation, useQuery } from '@tanstack/react-query';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { Outlet, useLocation, useMatch } from 'react-router-dom';

import {
  type Entity,
  identityEntityCreateBusinessRootEntityMutation,
  identityEntityCreatePersonRootEntityMutation,
  identityEntityGetRootEntityOptions,
  identityEntityUpdateAssociatedPersonsMutation,
  identityEntityUpdateBusinessEntityMutation,
  identityEntityUpdatePersonEntityMutation,
} from '@column/api';
import { Icon } from '@column/column-ui-kit';
import { Button } from '@column/ui';

import { CustomPageContext, PageTabs } from '~/app/Layout';
import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { useNavigate } from '~/lib/navigation';
import { useHelpSidebarStore } from '~/stores/HelpSidebar';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { ApiError } from '~/typings/API';
import { ApiErrCode } from '~/typings/enum';

import { createEntityModeRequestOptions } from './_util/entityApiMode';
import { Mode, RootEntityOutletContext } from './_util/RootEntityContext';
import { useAllAssociatedPersons } from './_util/useAllAssociatedPersons';
import {
  BusinessEntityFormValues,
  EntityFormType,
  PersonEntityFormValues,
  associatedPersonRecordToFormValues,
  buildBusinessCreateRequest,
  buildBusinessUpdateRequest,
  buildPersonCreateRequest,
  buildPersonUpdateRequest,
  buildUpdateAssociatedPersonsRequest,
  createDefaultAssociatedPerson,
  createDefaultPersonFormValues,
  createInitialBusinessFormState,
  getBusinessFormValuesFromEntity,
  getPersonFormValuesFromEntity,
  useEntityForm,
} from './_util/useEntityForm';
import { EntityTypes } from './Edit';

// Thin wrapper over the shared helper so the root queries/mutations and the associated-person
// selector/modal (via EntityApiModeProvider) build identical mode headers and can't drift.
const createModeRequestOptions = (platformId: string, mode: Mode) =>
  createEntityModeRequestOptions(platformId, mode === Mode.Live);

const shouldNotRetryOnNotFound = (failureCount: number, error: unknown) => {
  const apiError = error as Partial<ApiError> | undefined;
  if (String(apiError?.code) === ApiErrCode.ResourceNotFound.toString()) {
    return false;
  }
  return failureCount < 3;
};

export const PageRootEntitySection: FC<CustomPageContext> = ({ entries }) => {
  const { currentPlatform, isSandbox, isAuthorized } = useSessionStore();
  const openHelpSidebar = useHelpSidebarStore((state) => state.openHelpSidebar);
  const navigate = useNavigate();
  const location = useLocation();
  const isLiveRoute = useMatch(ROUTE.ENTITIES_ROOT_ENTITY_LIVE);
  const addSuccessNotification = useNotificationStore((state) => state.addSuccessNotification);
  const addDangerNotification = useNotificationStore((state) => state.addDangerNotification);
  const { handleSubmitErrors } = useFormErrorHandler();

  const mode = useMemo(() => (isLiveRoute ? Mode.Live : Mode.Sandbox), [isLiveRoute]);
  const [formType, setFormType] = useState<EntityTypes>();

  const createRootPersonMutation = useMutation(identityEntityCreatePersonRootEntityMutation());
  const createRootBusinessMutation = useMutation(identityEntityCreateBusinessRootEntityMutation());
  const updateRootPersonMutation = useMutation(identityEntityUpdatePersonEntityMutation());
  const updateRootBusinessMutation = useMutation(identityEntityUpdateBusinessEntityMutation());
  const updateRootAssociatedPersonsMutation = useMutation(identityEntityUpdateAssociatedPersonsMutation());

  const sandboxRootQueryOptions = identityEntityGetRootEntityOptions({
    path: { platform_id: currentPlatform?.id ?? '' },
    ...createModeRequestOptions(currentPlatform?.id ?? '', Mode.Sandbox),
  });
  const {
    data: sandboxRootEntity,
    isLoading: isSandboxLoading,
    refetch: refetchSandbox,
  } = useQuery({
    ...sandboxRootQueryOptions,
    enabled: !!currentPlatform?.id,
    staleTime: 5 * 60 * 1000,
    retry: shouldNotRetryOnNotFound,
  });

  const liveRootQueryOptions = identityEntityGetRootEntityOptions({
    path: { platform_id: currentPlatform?.id ?? '' },
    ...createModeRequestOptions(currentPlatform?.id ?? '', Mode.Live),
  });
  const {
    data: liveRootEntity,
    isLoading: isLiveLoading,
    refetch: refetchLive,
  } = useQuery({
    ...liveRootQueryOptions,
    enabled: !!currentPlatform?.id && !!currentPlatform?.isLiveEnabled,
    staleTime: 5 * 60 * 1000,
    retry: shouldNotRetryOnNotFound,
  });

  const rootEntityResponse = mode === Mode.Sandbox ? sandboxRootEntity : liveRootEntity;
  const form = useEntityForm(formType);

  // Existing business root entities carry their associated persons on a separate endpoint (the
  // entity response's beneficial_owners have no person_entity_id), so load them here to hydrate the
  // form and to reconcile against on save — mirroring the entity edit page.
  const isBusinessRoot = Boolean(rootEntityResponse?.business_details && rootEntityResponse?.id);
  const associatedPersons = useAllAssociatedPersons(rootEntityResponse?.id, {
    enabled: isBusinessRoot && !!currentPlatform?.id,
    requestOptions: createModeRequestOptions(currentPlatform?.id ?? '', mode),
    scope: [currentPlatform?.id, mode],
  });

  const currentRootType = useMemo<EntityTypes | undefined>(() => {
    if (rootEntityResponse?.person_details) return EntityFormType.Person;
    if (rootEntityResponse?.business_details) return EntityFormType.Business;
    return undefined;
  }, [rootEntityResponse]);

  const resetForm = useCallback(
    (entityType: EntityTypes, entity: Entity | null) => {
      if (entity) {
        const values =
          entityType === EntityFormType.Person
            ? getPersonFormValuesFromEntity(entity)
            : getBusinessFormValuesFromEntity(entity);
        // Hydrate the linked associated persons once they've loaded so each row carries its real
        // person_entity_id. resetForm depends on this data, so it re-runs (and re-seeds the rows)
        // when the query resolves.
        if (entityType === EntityFormType.Business && associatedPersons.isComplete) {
          const records = associatedPersons.associatedPersons;
          // Always show at least one associated-person form so a person can be linked
          // without first clicking "Add".
          (values as BusinessEntityFormValues).associatedPersons = records.length
            ? records.map(associatedPersonRecordToFormValues)
            : [createDefaultAssociatedPerson()];
        }
        form.reset(values);
      } else {
        const values =
          entityType === EntityFormType.Person ? createDefaultPersonFormValues() : createInitialBusinessFormState();
        form.reset(values);
      }
    },
    [form, associatedPersons.isComplete, associatedPersons.associatedPersons]
  );

  useEffect(() => {
    if (location.pathname === ROUTE.ENTITIES_ROOT_ENTITY) {
      navigate(isSandbox ? ROUTE.ENTITIES_ROOT_ENTITY_SANDBOX : ROUTE.ENTITIES_ROOT_ENTITY_LIVE, { replace: true });
    }
  }, [isSandbox, location.pathname, navigate]);

  useEffect(() => {
    if (currentRootType && currentRootType !== formType) {
      setFormType(currentRootType);
    }
  }, [currentRootType, formType]);

  const refetchCurrent = async () => {
    if (mode === Mode.Sandbox) {
      await refetchSandbox();
    } else {
      await refetchLive();
    }
  };

  const handleSubmit = form.handleSubmit(async () => {
    if (!formType) {
      addDangerNotification({ content: 'Please select an entity type.' });
      return;
    }

    try {
      if (formType === EntityFormType.Person) {
        const payload = buildPersonUpdateRequest(form.getValues() as PersonEntityFormValues);

        if (rootEntityResponse?.person_details && rootEntityResponse.id) {
          const response = await updateRootPersonMutation.mutateAsync({
            path: { entity_id: rootEntityResponse.id },
            body: payload,
            ...createModeRequestOptions(currentPlatform?.id ?? '', mode),
          });
          addSuccessNotification({
            content: `${response.person_details?.first_name ?? ''} ${response.person_details?.last_name ?? ''}`.trim()
              ? `${response.person_details?.first_name ?? ''} ${response.person_details?.last_name ?? ''} updated`
              : 'Root entity updated',
          });
          await refetchCurrent();
        } else {
          const createPayload = buildPersonCreateRequest(form.getValues() as PersonEntityFormValues);
          const response = await createRootPersonMutation.mutateAsync({
            path: { platform_id: currentPlatform?.id ?? '' },
            body: createPayload,
            ...createModeRequestOptions(currentPlatform?.id ?? '', mode),
          });
          addSuccessNotification({
            content: `${response.person_details?.first_name ?? ''} ${response.person_details?.last_name ?? ''}`.trim()
              ? `${response.person_details?.first_name ?? ''} ${response.person_details?.last_name ?? ''} created`
              : 'Root entity created',
          });
          await refetchCurrent();
          setFormType(EntityFormType.Person);
        }
      } else {
        const businessValues = form.getValues() as BusinessEntityFormValues;

        // Only submit the identification field the user actually changed: a root business that stores
        // both an EIN and a registration ID keeps both, but the API rejects them together in one
        // request, so a PATCH must send just the edited one.
        const dirtyFields = form.formState.dirtyFields as {
          ein?: boolean;
          registrationId?: boolean | Record<string, boolean>;
        };
        const registrationIdDirty =
          dirtyFields.registrationId === true ||
          (typeof dirtyFields.registrationId === 'object' && Object.values(dirtyFields.registrationId).some(Boolean));
        const changedIdentification = { ein: Boolean(dirtyFields.ein), registrationId: Boolean(registrationIdDirty) };

        // The reconcile endpoint rejects an empty set, so skip it when there are no associated persons.
        const associatedPersonsBody = buildUpdateAssociatedPersonsRequest(businessValues);
        const hasAssociatedPersons = associatedPersonsBody.associated_persons.length > 0;

        if (rootEntityResponse?.business_details && rootEntityResponse.id) {
          const payload = buildBusinessUpdateRequest(businessValues, changedIdentification);
          const response = await updateRootBusinessMutation.mutateAsync({
            path: { entity_id: rootEntityResponse.id },
            body: payload,
            ...createModeRequestOptions(currentPlatform?.id ?? '', mode),
          });
          // Reconcile associated persons (add/update/remove) to match the form. Guarded on a loaded
          // baseline so a failed associated-persons fetch can't wipe existing links with a blank form.
          if (associatedPersons.isComplete && hasAssociatedPersons) {
            await updateRootAssociatedPersonsMutation.mutateAsync({
              path: { entity_id: rootEntityResponse.id },
              body: associatedPersonsBody,
              ...createModeRequestOptions(currentPlatform?.id ?? '', mode),
            });
            await associatedPersons.refetch();
          }
          addSuccessNotification({
            content: response.business_details?.business_name
              ? `${response.business_details.business_name} updated`
              : 'Root entity updated',
          });
          await refetchCurrent();
        } else {
          const payload = buildBusinessCreateRequest(businessValues, { allowEinPending: false });
          const response = await createRootBusinessMutation.mutateAsync({
            path: { platform_id: currentPlatform?.id ?? '' },
            body: payload,
            ...createModeRequestOptions(currentPlatform?.id ?? '', mode),
          });
          // Link the selected associated persons onto the freshly created root business entity.
          if (response.id && hasAssociatedPersons) {
            await updateRootAssociatedPersonsMutation.mutateAsync({
              path: { entity_id: response.id },
              body: associatedPersonsBody,
              ...createModeRequestOptions(currentPlatform?.id ?? '', mode),
            });
          }
          addSuccessNotification({
            content: response.business_details?.business_name
              ? `${response.business_details.business_name} created`
              : 'Root entity created',
          });
          await refetchCurrent();
          setFormType(EntityFormType.Business);
        }
      }
    } catch (error: any) {
      const message = error?.message ?? 'Failed to save root entity';
      addDangerNotification({ content: message });
    }
  }, handleSubmitErrors);

  const headerRootEntity = mode === Mode.Sandbox ? sandboxRootEntity : liveRootEntity;
  const headerHasRootEntity = Boolean(headerRootEntity);

  const tabEntries = [
    {
      label: 'Live',
      path: ROUTE.ENTITIES_ROOT_ENTITY_LIVE,
    },
    {
      label: 'Sandbox',
      path: ROUTE.ENTITIES_ROOT_ENTITY_SANDBOX,
    },
  ];

  return (
    <FormProvider {...form}>
      <PageTabs entries={entries ?? tabEntries} />
      <PageActions>
        <Button variant="muted" size="icon-sm" onClick={() => openHelpSidebar('RootEntity')}>
          <Icon.CircleQuestionmark />
        </Button>
        {mode === Mode.Sandbox && (
          <Button
            onClick={handleSubmit}
            disabled={!isAuthorized({ permission: 'entities', permissionLevel: 'write' })}
            size="sm"
          >
            {!headerHasRootEntity && <Icon.Plus />}
            {headerHasRootEntity ? 'Save Changes' : 'Create Root Entity'}
          </Button>
        )}
      </PageActions>
      <Outlet
        context={
          {
            mode,
            formType,
            sandboxRootEntity,
            liveRootEntity,
            isSandboxLoading,
            isLiveLoading,
            isSandbox: isSandbox ?? false,
            handleSubmit,
            setFormType,
            resetForm,
          } satisfies RootEntityOutletContext
        }
      />
    </FormProvider>
  );
};
